/**
 * Agents console — the single pane for Brett's agent workforce inside
 * the planner. Access is per-user (`can_view_agents`): the nav entry,
 * the route, and every API call vanish without the flag (backend 404s),
 * so the page is undiscoverable to other users, admins included.
 *
 * Three blocks: the NEEDS-YOU feed (actionable in place — a kicker item
 * carries a Mark-received button that flips the same brief field
 * /intake's Kickstart badge reads), run health, and a report viewer.
 * Agent HQ's repo stays the system of record; this page renders + acts.
 */
import { useState } from "react";
import DOMPurify from "dompurify";
import { AlertTriangle, Bot, Check, ExternalLink, Inbox } from "lucide-react";
import { toast } from "sonner";
import { TopBar } from "@/components/layout/TopBar";
import { cn } from "@/lib/cn";
import {
  useAgentsFeed,
  useAgentReport,
  useAckAgentItem,
  useMarkKickerReceived,
  type AgentItem,
} from "@/hooks/useAgents";

const KIND_META: Record<string, { label: string; cls: string }> = {
  urgent: { label: "Urgent", cls: "bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 ring-red-200 dark:ring-red-500/30" },
  decide: { label: "Decide", cls: "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 ring-amber-200 dark:ring-amber-500/30" },
  kicker: { label: "Kicker", cls: "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-emerald-200 dark:ring-emerald-500/30" },
  fyi: { label: "FYI", cls: "bg-raised text-ink-3 ring-line" },
};

export function AgentsConsole() {
  const { data, isLoading, isError } = useAgentsFeed();
  const ack = useAckAgentItem();
  const markKicker = useMarkKickerReceived();
  const [openReport, setOpenReport] = useState<number | null>(null);
  const report = useAgentReport(openReport);

  const open = (data?.items ?? []).filter((i) => !i.ack);
  const acked = (data?.items ?? []).filter((i) => i.ack);

  const doAck = (item: AgentItem) =>
    ack.mutate(item.key, {
      onSuccess: () => toast.success("Acknowledged"),
      onError: () => toast.error("Couldn't acknowledge"),
    });

  const doKicker = (item: AgentItem) => {
    if (!item.project_id) return doAck(item);
    markKicker.mutate(
      { projectId: item.project_id, itemKey: item.key },
      {
        onSuccess: () =>
          toast.success("Brief marked received — /intake's Kickstart badge is updated"),
        onError: () => toast.error("Couldn't update the brief"),
      },
    );
  };

  return (
    <>
      <TopBar
        title="Agents"
        subtitle="Your agent workforce — what needs you, and how the runs are going. Visible only to accounts with agent access."
      />
      <div className="space-y-6 p-8">
        {isLoading && (
          <div className="rounded-xl border border-line bg-surface p-8 text-center text-sm text-ink-muted">
            Loading…
          </div>
        )}
        {isError && (
          <div className="rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-6 text-sm text-red-800 dark:text-red-300">
            Couldn't load the agents feed.
          </div>
        )}

        {data && (
          <>
            {/* Needs you */}
            <section className="overflow-hidden rounded-xl border border-line bg-surface shadow-sm">
              <div className="border-b border-line-soft px-5 py-3">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Needs you <span className="text-ink-subtle">({open.length})</span>
                </h2>
                <p className="text-[11px] text-ink-muted">
                  Items your agents flagged for a human decision. Actions here write
                  to the planner directly — a kicker marked received updates /intake.
                </p>
              </div>
              {open.length === 0 ? (
                <div className="px-5 py-8 text-center text-xs text-ink-subtle">
                  Nothing waiting on you.
                </div>
              ) : (
                <ul className="divide-y divide-line-soft">
                  {open.map((item) => {
                    const meta = KIND_META[item.kind] ?? KIND_META.fyi;
                    return (
                      <li key={item.key} className="flex items-center gap-3 px-5 py-3">
                        <span className={cn("inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1", meta.cls)}>
                          {meta.label}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-ink">{item.title}</div>
                          <div className="truncate text-[10px] text-ink-subtle">
                            {item.agent} · {new Date(item.reported_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                            {item.detail && ` — ${item.detail}`}
                          </div>
                        </div>
                        {item.link && (
                          <a href={item.link} target="_blank" rel="noreferrer"
                             className="shrink-0 inline-flex items-center gap-1 text-[11px] text-sky-700 dark:text-sky-300 hover:underline">
                            Open <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                        {item.kind === "kicker" && item.project_id ? (
                          <button
                            onClick={() => doKicker(item)}
                            disabled={markKicker.isPending}
                            className="shrink-0 inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                            title={`Set ${item.project_id}'s kickstart brief to submitted and clear this item`}
                          >
                            <Check className="h-3 w-3" /> Mark received
                          </button>
                        ) : (
                          <button
                            onClick={() => doAck(item)}
                            disabled={ack.isPending}
                            className="shrink-0 rounded-md border border-line px-2.5 py-1 text-[11px] font-medium text-ink-3 hover:border-line-strong hover:bg-raised disabled:opacity-50"
                            title="Acknowledge — clears it from this list; the agent's log keeps the record"
                          >
                            Acknowledge
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {acked.length > 0 && (
                <details className="border-t border-line-soft px-5 py-3">
                  <summary className="cursor-pointer text-xs font-medium text-ink-muted">
                    Acknowledged ({acked.length})
                  </summary>
                  <ul className="mt-2 space-y-1">
                    {acked.map((i) => (
                      <li key={i.key} className="flex items-center gap-2 py-1 text-xs text-ink-subtle">
                        <Check className="h-3 w-3 text-emerald-500" />
                        <span className="line-through">{i.title}</span>
                        <span>· {i.ack?.acked_by}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </section>

            {/* Run health */}
            <section className="overflow-hidden rounded-xl border border-line bg-surface shadow-sm">
              <div className="border-b border-line-soft px-5 py-3">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
                  <Bot className="h-4 w-4 text-ink-subtle" />
                  Recent runs <span className="text-ink-subtle">({data.runs.length})</span>
                </h2>
                <p className="text-[11px] text-ink-muted">
                  Every report your agents posted. Click one to read the full brief.
                </p>
              </div>
              {data.runs.length === 0 ? (
                <div className="px-5 py-8 text-center text-xs text-ink-subtle">
                  <Inbox className="mx-auto mb-2 h-5 w-5 text-ink-faint" />
                  No runs reported yet — the next morning brief will appear here.
                </div>
              ) : (
                <ul className="divide-y divide-line-soft">
                  {data.runs.map((r) => (
                    <li key={r.id}>
                      <button
                        onClick={() => setOpenReport(openReport === r.id ? null : r.id)}
                        className="flex w-full items-center gap-3 px-5 py-2.5 text-left hover:bg-raised/60"
                      >
                        <span
                          className={cn(
                            "h-2 w-2 shrink-0 rounded-full",
                            r.status === "ok" ? "bg-emerald-500" : r.status === "blocked" ? "bg-amber-500" : "bg-red-500",
                          )}
                          title={r.status}
                        />
                        <span className="shrink-0 font-mono text-[10px] text-ink-subtle">
                          {new Date(r.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        </span>
                        <span className="shrink-0 text-xs font-medium text-ink-3">{r.agent}</span>
                        {r.run_kind === "manual" && (
                          <span className="shrink-0 rounded-full bg-raised px-1.5 py-0.5 text-[9px] font-semibold text-ink-muted">manual</span>
                        )}
                        <span className="min-w-0 flex-1 truncate text-xs text-ink-muted">{r.headline}</span>
                      </button>
                      {openReport === r.id && report.data?.html && (
                        <div
                          className="border-t border-line-soft bg-canvas/40 px-5 py-4"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(report.data.html, { FORBID_TAGS: ["script", "style", "iframe"] }),
                          }}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </>
  );
}
